import { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { useCallback } from 'react';
import { FindIndividualsDocument, FindIndividualsQuery, FindIndividualsQueryVariables } from 'gql';
import { fetchData } from 'gql/graphql-fetcher';
import { ProgressCallback, useExcelExport } from '../../../utils/dataGridExport';

const exportPageSize = 500;

type IndividualRow = NonNullable<NonNullable<FindIndividualsQuery['searchIndividuals']>['items']>[number];

interface Params {
  columns?: GridColDef<IndividualRow>[];
  columnVisibilityModel?: GridColumnVisibilityModel;
  queryOptions: Omit<FindIndividualsQueryVariables, 'pageSize' | 'skip'>;
  totalCount: number;
  isFetchingIndividuals: boolean;
}

export const useExportIndividualsAsExcel = ({
  columns,
  columnVisibilityModel,
  queryOptions,
  totalCount,
  isFetchingIndividuals
}: Params) => {
  const fetchAllIndividuals = useCallback(async (onProgress: ProgressCallback) => {
    if (totalCount === 0) return [];

    const allItems: IndividualRow[] = [];
    let skipCount = 0;

    while (skipCount < totalCount) {
      const response = await fetchData<FindIndividualsQuery, FindIndividualsQueryVariables>(FindIndividualsDocument, {
        ...queryOptions,
        pageSize: Math.min(exportPageSize, totalCount - skipCount),
        skip: skipCount
      })();

      const items = response?.searchIndividuals?.items ?? [];
      allItems.push(...items);

      if (items.length === 0) {
        break;
      }

      skipCount += items.length;
      onProgress({ phase: 'fetching', percentage: Math.round((skipCount / totalCount) * 100) });
    }

    return allItems;
  }, [queryOptions, totalCount]);

  const { handleExport, exporting, exportDisabled: baseExportDisabled, progress } = useExcelExport<IndividualRow>({
    columns,
    columnVisibilityModel,
    fetchAll: fetchAllIndividuals,
    fileNamePrefix: 'individuals'
  });

  const exportDisabled = baseExportDisabled || isFetchingIndividuals || totalCount === 0;

  return {
    handleExport,
    exporting,
    exportDisabled,
    progress
  };
};

